import React, { useState } from "react";

import StatusBadge from "../StatusBadge";
import { useToast } from "../../context/ToastContext";
import {
  sendClientReminder,
  updateHouseholdStatus,
} from "../../api/needsAttentionAPI";
import { formatCompactCurrency, formatRelative } from "../../utils/formatters";

const HouseholdCard = ({ card, projectId, navigate, actions, onActionComplete }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const navigateToHouseholdDetail = () =>
    navigate(`/project/${projectId}/house/${card.id}`);

  const refresh = () => {
    if (typeof onActionComplete === "function") onActionComplete();
  };

  const handleKickOff = () => {
    if (actions && typeof actions.onKickOff === "function") {
      actions.onKickOff(card);
      return;
    }
    navigateToHouseholdDetail();
  };

  const handleSendReminder = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await sendClientReminder(card.id);
      toast.success(`Reminder sent to ${card.name}.`);
      refresh();
    } catch (err) {
      toast.fromError(err, "We could not send the reminder.");
    } finally {
      setBusy(false);
    }
  };

  const handleInitiateDocuSign = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateHouseholdStatus(card.id, { status: "docusign_initiated" });
      toast.success(`DocuSign initiated for ${card.name}.`);
      refresh();
    } catch (err) {
      toast.fromError(err, "We could not initiate DocuSign right now.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrimaryCta = (e) => {
    e.stopPropagation();
    switch (card.cta_key) {
      case "kick_off":
        return handleKickOff();
      case "send_reminder":
        return handleSendReminder();
      case "initiate_docusign":
        return handleInitiateDocuSign();
      case "resolve":
        return navigate(
          `/project/${projectId}/house/${card.id}?panel=accounts&filter=nigo`,
        );
      case "resolve_query":
      case "open":
      default:
        return navigateToHouseholdDetail();
    }
  };

  const ctaLabel = (() => {
    switch (card.cta_key) {
      case "kick_off":
        return "Kick off";
      case "send_reminder":
        return busy ? "Sending…" : "Send reminder";
      case "initiate_docusign":
        return busy ? "Initiating…" : "Initiate DocuSign";
      case "resolve":
        return "Resolve";
      case "resolve_query":
        return "Resolve query";
      case "open":
      default:
        return "Open";
    }
  })();

  const isFilledCta =
    card.cta_key === "kick_off" ||
    card.cta_key === "send_reminder" ||
    card.cta_key === "initiate_docusign" ||
    card.cta_key === "resolve" ||
    card.cta_key === "resolve_query";

  const isResolveCta = card.cta_key === "resolve";

  const lastUpdatedAt = card.last_activity_at;

  const handleCardClick = () => {
    if (busy) return;
    if (card.cta_key === "kick_off") {
      handleKickOff();
      return;
    }
    navigateToHouseholdDetail();
  };

  return (
    <div
      onClick={handleCardClick}
      className="p-3 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {card.name}
          </h4>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {card.member_count} member{card.member_count === 1 ? "" : "s"}
            {" · "}
            {card.account_count} account{card.account_count === 1 ? "" : "s"}
            {card.advisor_first_name ? ` · ${card.advisor_first_name}` : ""}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {card.aum != null && (
            <div className="text-sm font-bold text-gray-900">
              ${formatCompactCurrency(card.aum)}
            </div>
          )}
          <StatusBadge status={card.status} subStatus={card.sub_status} />
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-dashed border-gray-200 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          {card.unread_comments && card.unread_comments.length > 0 ? (
            <div className="space-y-0.5">
              {card.unread_comments.map((c, i) => (
                <div key={i} className="text-[11px] text-gray-800 truncate">
                  <span className="font-semibold">{c.author_name}:</span>{" "}
                  <span className="text-gray-600">{c.message}</span>
                </div>
              ))}
              {card.unread_comment_count > card.unread_comments.length && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToHouseholdDetail();
                  }}
                  className="text-[11px] font-medium text-blue-600 hover:text-blue-700"
                >
                  +{card.unread_comment_count - card.unread_comments.length} more
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="text-xs font-medium text-gray-800 truncate">
                {card.reason_primary}
              </div>
              {card.reason_secondary && (
                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                  {card.reason_secondary}
                </div>
              )}
              {card.nigo_notes && (
                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                  {card.nigo_notes}
                </div>
              )}
            </>
          )}
          {lastUpdatedAt && (
            <div className="text-[10px] text-gray-400 mt-1">
              Last updated {formatRelative(lastUpdatedAt)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handlePrimaryCta}
          disabled={busy}
          className={
            isResolveCta
              ? "flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              : isFilledCta
              ? "flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              : "flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
          }
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default HouseholdCard;
